import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
  useRef,
} from 'react';
import { api, APIError, ErrorCode, UserPermissions, TabPermissions } from '../services/api-client';

// =============================================================================
// Types
// =============================================================================

declare global {
  interface Window {
    google: any;
    handleCredentialResponse: (response: any) => void;
  }
}

export interface User {
  email: string;
  name: string;
  role: 'viewer' | 'developer' | 'admin';
  domain: string;
  created_at?: string;
  last_login?: string;
  token_expires_at?: number;
  effective_permissions?: UserPermissions;
}

interface AuthContextType {
  user: User | null;
  loading: boolean;
  error: string | null;
  errorCode: string | null;
  isAuthenticated: boolean;
  canManagePermissions: boolean;
  canManageUsers: boolean;
  googleInitialized: boolean;
  signIn: () => void;
  signOut: () => void;
  showTimeoutWarning: boolean;
  stayLoggedIn: () => void;
  refreshToken: () => Promise<boolean>;
  tokenExpiresAt: number | null;
  // Fine-grained permission helpers
  hasPermission: (tab: keyof UserPermissions, action: keyof TabPermissions) => boolean;
  getTabPermissions: (tab: keyof UserPermissions) => TabPermissions;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

// =============================================================================
// Configuration
// =============================================================================

const GOOGLE_CLIENT_ID =
  import.meta.env.VITE_GOOGLE_CLIENT_ID ||
  '55189446448-071fje6rrbtl4s0knb8u49uo2t3q52ts.apps.googleusercontent.com';

// Inactivity timeout settings
const WARNING_TIME_MS = 25 * 60 * 1000; // 25 minutes
const TIMEOUT_TIME_MS = 30 * 60 * 1000; // 30 minutes

// Token refresh buffer (refresh 5 minutes before expiry)
const TOKEN_REFRESH_BUFFER_MS = 5 * 60 * 1000;

// =============================================================================
// Provider Component
// =============================================================================

export function GoogleAuthProvider({ children }: { children: React.ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [errorCode, setErrorCode] = useState<string | null>(null);
  const [googleLoaded, setGoogleLoaded] = useState(false);
  const [initialized, setInitialized] = useState(false);
  const [lastActivity, setLastActivity] = useState(Date.now());
  const [showTimeoutWarning, setShowTimeoutWarning] = useState(false);
  const [tokenExpiresAt, setTokenExpiresAt] = useState<number | null>(null);

  const signOutRef = useRef<() => void>();
  const tokenRefreshTimerRef = useRef<NodeJS.Timeout | null>(null);

  // ---------------------------------------------------------------------------
  // Token Refresh
  // ---------------------------------------------------------------------------

  /**
   * Attempt to silently refresh the Google token
   * Returns the new token or null if refresh failed
   */
  const refreshToken = useCallback(async (): Promise<boolean> => {
    if (!window.google) {
      console.warn('Google SDK not loaded, cannot refresh token');
      return false;
    }

    return new Promise((resolve) => {
      try {
        // Use Google's silent sign-in to get a fresh token
        window.google.accounts.id.prompt((notification: any) => {
          if (notification.isNotDisplayed() || notification.isSkippedMoment()) {
            console.log('Silent refresh not possible, user interaction required');
            resolve(false);
          }
          // If prompt is displayed, the callback will handle the new token
        });

        // Set a timeout for the refresh attempt
        setTimeout(() => resolve(false), 5000);
      } catch (err) {
        console.error('Token refresh error:', err);
        resolve(false);
      }
    });
  }, []);

  /**
   * Get a fresh token for API calls
   * This is passed to the API client for automatic refresh
   */
  const getRefreshedToken = useCallback(async (): Promise<string | null> => {
    const storedToken = sessionStorage.getItem('google_auth_token');
    if (!storedToken) return null;

    // Check if we can use the existing token
    const exp = api.getTokenExpiresAt();
    if (exp && Date.now() < exp * 1000 - TOKEN_REFRESH_BUFFER_MS) {
      return storedToken;
    }

    // Try to refresh
    const refreshed = await refreshToken();
    if (refreshed) {
      return sessionStorage.getItem('google_auth_token');
    }

    return storedToken; // Return old token, let backend reject if expired
  }, [refreshToken]);

  // Register token refresh callback with API client
  useEffect(() => {
    api.setTokenRefreshCallback(getRefreshedToken);
  }, [getRefreshedToken]);

  // ---------------------------------------------------------------------------
  // Handle Google Sign-In Response
  // ---------------------------------------------------------------------------

  const handleCredentialResponse = useCallback(async (response: any) => {
    try {
      setLoading(true);
      setError(null);
      setErrorCode(null);

      const token = response.credential;

      // Store token
      sessionStorage.setItem('google_auth_token', token);
      api.setAuthToken(token);

      // Get user profile from backend
      console.log('Calling backend with token...');
      const userProfile = await api.getCurrentUser();
      console.log('User profile received:', userProfile);

      setUser(userProfile);
      setTokenExpiresAt(userProfile.token_expires_at || null);
      setLastActivity(Date.now());

      // Schedule token refresh
      if (userProfile.token_expires_at) {
        scheduleTokenRefresh(userProfile.token_expires_at);
      }
    } catch (err) {
      console.error('Authentication error:', err);

      if (err instanceof APIError) {
        setError(err.message);
        setErrorCode(err.code);

        // If token expired, clear session and prompt re-login
        if (err.isTokenExpired) {
          sessionStorage.removeItem('google_auth_token');
          api.clearAuthToken();
        }
      } else {
        setError(err instanceof Error ? err.message : 'Authentication failed');
        setErrorCode(null);
      }

      setUser(null);
    } finally {
      setLoading(false);
    }
  }, []);

  // ---------------------------------------------------------------------------
  // Token Refresh Scheduling
  // ---------------------------------------------------------------------------

  const scheduleTokenRefresh = useCallback((expiresAt: number) => {
    // Clear existing timer
    if (tokenRefreshTimerRef.current) {
      clearTimeout(tokenRefreshTimerRef.current);
    }

    const now = Date.now();
    const expiresAtMs = expiresAt * 1000;
    const refreshAt = expiresAtMs - TOKEN_REFRESH_BUFFER_MS;

    if (refreshAt > now) {
      const delay = refreshAt - now;
      console.log(`Scheduling token refresh in ${Math.round(delay / 1000 / 60)} minutes`);

      tokenRefreshTimerRef.current = setTimeout(async () => {
        console.log('Attempting scheduled token refresh...');
        const success = await refreshToken();
        if (!success) {
          console.warn('Scheduled token refresh failed');
          // Token will be refreshed on next API call or user will be prompted
        }
      }, delay);
    }
  }, [refreshToken]);

  // ---------------------------------------------------------------------------
  // Session Restoration
  // ---------------------------------------------------------------------------

  useEffect(() => {
    const checkExistingSession = async () => {
      const storedToken = sessionStorage.getItem('google_auth_token');
      if (storedToken) {
        try {
          setLoading(true);
          api.setAuthToken(storedToken);
          const userProfile = await api.getCurrentUser();
          setUser(userProfile);
          setTokenExpiresAt(userProfile.token_expires_at || null);
          setLastActivity(Date.now());

          if (userProfile.token_expires_at) {
            scheduleTokenRefresh(userProfile.token_expires_at);
          }
        } catch (err) {
          console.error('Failed to restore session:', err);

          if (err instanceof APIError && err.isTokenExpired) {
            // Token expired, try to refresh
            console.log('Stored token expired, attempting refresh...');
            // Don't clear yet, let the user try to sign in again
          }

          sessionStorage.removeItem('google_auth_token');
          api.clearAuthToken();
        } finally {
          setLoading(false);
        }
      } else {
        setLoading(false);
      }
    };

    checkExistingSession();

    // Cleanup timer on unmount
    return () => {
      if (tokenRefreshTimerRef.current) {
        clearTimeout(tokenRefreshTimerRef.current);
      }
    };
  }, [scheduleTokenRefresh]);

  // ---------------------------------------------------------------------------
  // Activity Tracking
  // ---------------------------------------------------------------------------

  useEffect(() => {
    const handleActivity = () => {
      setLastActivity(Date.now());
      setShowTimeoutWarning(false);
    };

    window.addEventListener('mousedown', handleActivity);
    window.addEventListener('keydown', handleActivity);
    window.addEventListener('scroll', handleActivity);
    window.addEventListener('touchstart', handleActivity);

    return () => {
      window.removeEventListener('mousedown', handleActivity);
      window.removeEventListener('keydown', handleActivity);
      window.removeEventListener('scroll', handleActivity);
      window.removeEventListener('touchstart', handleActivity);
    };
  }, []);

  // ---------------------------------------------------------------------------
  // Inactivity Timer
  // ---------------------------------------------------------------------------

  useEffect(() => {
    if (!user) return;

    const checkInactivity = () => {
      const now = Date.now();
      const timeSinceActivity = now - lastActivity;

      if (timeSinceActivity >= TIMEOUT_TIME_MS) {
        if (signOutRef.current) {
          signOutRef.current();
        }
      } else if (timeSinceActivity >= WARNING_TIME_MS && !showTimeoutWarning) {
        setShowTimeoutWarning(true);
      }
    };

    const interval = setInterval(checkInactivity, 10000);
    return () => clearInterval(interval);
  }, [user, lastActivity, showTimeoutWarning]);

  // ---------------------------------------------------------------------------
  // Load Google Identity Services
  // ---------------------------------------------------------------------------

  useEffect(() => {
    const script = document.createElement('script');
    script.src = 'https://accounts.google.com/gsi/client';
    script.async = true;
    script.defer = true;
    script.onload = () => {
      console.log('Google Sign-In script loaded');
      setGoogleLoaded(true);
    };
    script.onerror = (error) => {
      console.error('Failed to load Google Sign-In script:', error);
      setError('Failed to load Google Sign-In');
    };
    document.body.appendChild(script);

    return () => {
      if (document.body.contains(script)) {
        document.body.removeChild(script);
      }
    };
  }, []);

  // ---------------------------------------------------------------------------
  // Initialize Google Sign-In
  // ---------------------------------------------------------------------------

  useEffect(() => {
    if (!googleLoaded || !window.google || initialized) return;

    try {
      console.log('Initializing with client ID:', GOOGLE_CLIENT_ID);

      window.google.accounts.id.initialize({
        client_id: GOOGLE_CLIENT_ID,
        callback: handleCredentialResponse,
        auto_select: false,
        cancel_on_tap_outside: false,
        use_fedcm_for_prompt: true,
      });

      setInitialized(true);

      // Show prompt if no existing session
      const storedToken = sessionStorage.getItem('google_auth_token');
      if (!storedToken && !loading) {
        window.google.accounts.id.prompt((notification: any) => {
          console.log('One Tap notification:', notification);
        });
      }
    } catch (err) {
      console.error('Failed to initialize Google Sign-In:', err);
      setError('Failed to initialize authentication');
    }
  }, [googleLoaded, handleCredentialResponse, initialized, loading]);

  // ---------------------------------------------------------------------------
  // Sign In / Sign Out
  // ---------------------------------------------------------------------------

  const signIn = () => {
    if (window.google) {
      window.google.accounts.id.prompt();
    }
  };

  const signOut = useCallback(() => {
    if (window.google) {
      window.google.accounts.id.disableAutoSelect();
    }
    setUser(null);
    setTokenExpiresAt(null);
    setError(null);
    setErrorCode(null);
    api.clearAuthToken();
    sessionStorage.removeItem('google_auth_token');
    setShowTimeoutWarning(false);

    if (tokenRefreshTimerRef.current) {
      clearTimeout(tokenRefreshTimerRef.current);
    }
  }, []);

  const stayLoggedIn = () => {
    setLastActivity(Date.now());
    setShowTimeoutWarning(false);
  };

  useEffect(() => {
    signOutRef.current = signOut;
  }, [signOut]);

  // ---------------------------------------------------------------------------
  // Fine-Grained Permission Helpers
  // ---------------------------------------------------------------------------

  const hasPermission = useCallback(
    (tab: keyof UserPermissions, action: keyof TabPermissions): boolean => {
      if (!user) return false;
      // Admin always has full access
      if (user.role === 'admin') return true;
      // Check effective permissions
      const tabPerms = user.effective_permissions?.[tab];
      return tabPerms?.[action] ?? false;
    },
    [user]
  );

  const getTabPermissions = useCallback(
    (tab: keyof UserPermissions): TabPermissions => {
      if (!user) return {};
      // Admin always has full access
      if (user.role === 'admin') {
        return { view: true, edit: true, upload: true, sync: true };
      }
      return user.effective_permissions?.[tab] ?? {};
    },
    [user]
  );

  // ---------------------------------------------------------------------------
  // Context Value
  // ---------------------------------------------------------------------------

  const value: AuthContextType = {
    user,
    loading,
    error,
    errorCode,
    isAuthenticated: !!user,
    canManagePermissions: user?.role === 'developer' || user?.role === 'admin' || false,
    canManageUsers: user?.role === 'admin' || false,
    googleInitialized: initialized,
    signIn,
    signOut,
    showTimeoutWarning,
    stayLoggedIn,
    refreshToken,
    tokenExpiresAt,
    hasPermission,
    getTabPermissions,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

// =============================================================================
// Hook
// =============================================================================

export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within GoogleAuthProvider');
  }
  return context;
}
